import './impersonation'; // Must be first — handles admin impersonation token swap before React renders
// The global Tailwind stylesheet used to ride along for free because
// Homepage.js -> components/post -> this file's import chain was eagerly
// bundled into main.js. Now that most routes (including Homepage) are
// React.lazy, that chain no longer runs on every page — import it directly
// here so it's always in the main chunk regardless of which route loads.
import './index.css';
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
// import Picker from "./pick";
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store'; // Import your store and persistor
import AuthWrapper from './auth/AuthWrapper';
import PrivateRoute from './components/PrivateRoute';
import Logout from "./components/logout";
import { LiveStreamProvider } from "./components/LiveStream/LiveStreamContext";
import AuthProvider from "./components/auth/AuthProvider";
import CreatorRoute from "./components/auth/CreatorRoute";
import PermissionRoute from "./components/auth/PermissionRoute";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AppProvider } from "./contexts/AppContext";
import { SiteConfigProvider } from "./contexts/SiteConfigContext";
import { StoryProvider } from "./contexts/StoryContext";
import { VideoCallProvider } from "./contexts/VideoCallContext";
import VideoCallRoot from "./components/VideoCall/VideoCallRoot";
import { i18nReady } from './i18n';
import { getApiUrl } from './config/api';
import LandingPage from "./pages/LandingPage";
import ToastNotificationManager from "./components/ToastNotificationManager";
import PaymentRedirectToast from "./components/PaymentRedirectToast";
import AgeGateModal from "./components/AgeGateModal";
import ImpersonationBanner from "./components/Admin/ImpersonationBanner";
import InstallPwaBanner from "./components/PWA/InstallPwaBanner";
import InstallGate from "./pages/Install/InstallGate";

// Everything below is route-level and only needed once a visitor navigates
// past the public entry pages (landing/login/register/etc. above), so it's
// code-split out of the main chunk via React.lazy — otherwise an anonymous
// landing-page visitor would download the entire app (admin panel, CRM,
// statistics/recharts, chat, vaults, shop...) before seeing anything.
const Homepage = React.lazy(() => import("./pages/Homepage"));
const Settings = React.lazy(() => import("./components/settings"));
const Settingsprofile = React.lazy(() => import("./components/settings/Settingsprofile"));
const Accountsets = React.lazy(() => import("./components/settings/Account"));
const Notifications = React.lazy(() => import("./components/Notifications"));
const All = React.lazy(() => import("./components/Notifications/All"));
const Chats = React.lazy(() => import("./components/chats"));
const Openmsg = React.lazy(() => import("./components/chats/openmsg"));
const MassMessage = React.lazy(() => import("./components/chats/MassMessage"));
const Vaults = React.lazy(() => import("./components/Vaults"));
const Allmedia = React.lazy(() => import("./components/Vaults/Allmedia"));
const PayLinks = React.lazy(() => import("./components/PayLinks/PayLinks"));
const PayLinkStats = React.lazy(() => import("./components/PayLinks/PayLinkStats"));
const PayLinkPublic = React.lazy(() => import("./pages/PayLinkPublic"));
const Collections = React.lazy(() => import("./components/collections"));
const Userlist = React.lazy(() => import("./components/collections/userlist"));
const Bookmarklist = React.lazy(() => import("./components/collections/Bookmarklist"));
const ListDetail = React.lazy(() => import("./components/collections/ListDetail"));
const UserComments = React.lazy(() => import("./components/collections/UserComments"));
const PlansManagement = React.lazy(() => import("./components/Plans"));
const EarningsStatementPage = React.lazy(() => import("./components/Statements"));
const CreatorDashboard = React.lazy(() => import("./components/Statistics"));
const RechartsChartTypes = React.lazy(() => import("./components/Statistics/rechar"));
const CreatorStatsDashboard = React.lazy(() => import("./components/Statistics/overview"));
const EngagementStatistics = React.lazy(() => import("./components/Statistics/engagements"));
const ReachStatistics = React.lazy(() => import("./components/Statistics/Reach"));
const StatisticsPage = React.lazy(() => import("./components/Statistics/Fans"));
const QueueCalendar = React.lazy(() => import("./components/queue"));
const BookmarksPage = React.lazy(() => import("./components/bookmarks"));
const StatisticsDashboard = React.lazy(() => import("./components/Statistics/test"));
const PaymentCardsPage = React.lazy(() => import("./components/PaymentCard"));
const HelpCenter = React.lazy(() => import("./components/help"));
const PrivacyAndSafetySettings = React.lazy(() => import("./components/settings/privacy"));
const SubscriptionSettings = React.lazy(() => import("./components/settings/Subscriptions"));
const FansFollowingSettings = React.lazy(() => import("./components/settings/fansfollow"));
const NotificationSettings = React.lazy(() => import("./components/settings/notifications"));
const ChatSettings = React.lazy(() => import("./components/settings/chatsettings"));
const CreatorAiAgentSettings = React.lazy(() => import("./components/settings/AiAgentSettings"));
const StreamingSettings = React.lazy(() => import("./components/settings/streamsettings"));
const QrCodeSettings = React.lazy(() => import("./components/settings/qrcode"));
const DisplaySettings = React.lazy(() => import("./components/settings/display"));
const StorySettings = React.lazy(() => import("./components/settings/story"));
const SocialMedia = React.lazy(() => import("./components/settings/SocialMedia"));
// const Spotify = React.lazy(() => import("./components/settings/Spotify"));
// const SpringStore = React.lazy(() => import("./components/settings/SpringStore"));
// const ThingsILike = React.lazy(() => import("./components/settings/ThingsILike"));
// const Web3Avatar = React.lazy(() => import("./components/settings/Web3Avatar"));
// const NFTs = React.lazy(() => import("./components/settings/NFTs"));
const MySubscriptions = React.lazy(() => import("./components/settings/MySubscriptions"));
const MySubscribers = React.lazy(() => import("./components/settings/MySubscribers"));
const PhoneNumberSettings = React.lazy(() => import("./components/settings/PhoneNumber"));
const BecomeCreator = React.lazy(() => import("./pages/BecomeCreator"));
const PaymentSuccess = React.lazy(() => import("./pages/PaymentSuccess"));
const TeamInvitePage = React.lazy(() => import("./pages/TeamInvitePage"));
const AgencyApply = React.lazy(() => import("./pages/Agency/AgencyApply"));
const AgencyDashboard = React.lazy(() => import("./pages/Agency/AgencyDashboard"));
const AgencyInviteLanding = React.lazy(() => import("./pages/Agency/AgencyInviteLanding"));
const AgencyInbox = React.lazy(() => import("./pages/Agency/AgencyInbox"));
const AgencyOwnerSettings = React.lazy(() => import("./pages/Agency/AgencySettings"));
const SinglePost = React.lazy(() => import("./pages/SinglePost"));
const UserProfile = React.lazy(() => import("./pages/UserProfile"));
const WalletPage = React.lazy(() => import("./pages/WalletPage"));
const BankingPage = React.lazy(() => import("./pages/BankingPage"));

// Admin Pages
const AdminLayout = React.lazy(() => import("./pages/Admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("./pages/Admin/Dashboard"));
const AdminUsers = React.lazy(() => import("./pages/Admin/Users"));
const AdminUserEdit = React.lazy(() => import("./pages/Admin/AdminUserEdit"));
const PaymentSettings = React.lazy(() => import("./components/Admin/PaymentSettings"));
const AdminSettings = React.lazy(() => import("./components/Admin/AdminSettings"));
const AdminNotificationSettings = React.lazy(() => import("./components/Admin/AdminNotificationSettings"));
const AdminEmailSettings = React.lazy(() => import("./components/Admin/AdminEmailSettings"));
const LandingPageSettings = React.lazy(() => import("./components/Admin/LandingPageSettings"));
const PayoutManagement = React.lazy(() => import("./components/Admin/PayoutManagement"));
const StorageSettings = React.lazy(() => import("./components/Admin/StorageSettings"));
const CreatorVerifications = React.lazy(() => import("./components/Admin/CreatorVerifications"));
const AppearanceSettings = React.lazy(() => import("./components/Admin/AppearanceSettings"));
const PwaSettings = React.lazy(() => import("./components/Admin/PwaSettings"));
const AgencyApplications = React.lazy(() => import("./components/Admin/AgencyApplications"));
const AgencySettings = React.lazy(() => import("./components/Admin/AgencySettings"));
const AuthenticationSettings = React.lazy(() => import("./components/Admin/AuthenticationSettings"));
const ModerationPage = React.lazy(() => import("./components/Admin/ModerationPage"));
const MessagesAdmin = React.lazy(() => import("./components/Admin/MessagesAdmin"));
const AnalyticsPage = React.lazy(() => import("./components/Admin/AnalyticsPage"));
const ReportsPage = React.lazy(() => import("./components/Admin/ReportsPage"));
const IntegrationSettings = React.lazy(() => import("./components/Admin/IntegrationSettings"));
const LivestreamSettings = React.lazy(() => import("./components/Admin/LivestreamSettings"));
const AiAgentSettings = React.lazy(() => import("./components/Admin/AiAgentSettings"));
const LicenseSettings = React.lazy(() => import("./components/Admin/LicenseSettings"));
const PostsManagement = React.lazy(() => import("./components/Admin/PostsManagement"));
const SubscriptionsManagement = React.lazy(() => import("./components/Admin/SubscriptionsManagement"));
const TransactionsManagement = React.lazy(() => import("./components/Admin/TransactionsManagement"));
const AnnouncementsManagement = React.lazy(() => import("./components/Admin/AnnouncementsManagement"));
const ContactMessagesManagement = React.lazy(() => import("./components/Admin/ContactMessagesManagement"));
const HelpManagement = React.lazy(() => import("./components/Admin/HelpManagement"));
const MaintenanceMode = React.lazy(() => import("./components/Admin/MaintenanceMode"));
const ShopManagement = React.lazy(() => import("./components/Admin/ShopManagement"));
const GiftManagement = React.lazy(() => import("./components/Admin/GiftManagement"));
const CustomCode = React.lazy(() => import("./components/Admin/CustomCode"));
const AdminPlatformSettings = React.lazy(() => import("./components/Admin/AdminPlatformSettings"));
const PagesManagement = React.lazy(() => import("./components/Admin/PagesManagement"));
const StaticPage = React.lazy(() => import("./pages/StaticPage"));
const TermsOfService = React.lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = React.lazy(() => import("./pages/legal/PrivacyPolicy"));
const CookiePolicy = React.lazy(() => import("./pages/legal/CookiePolicy"));
const EmailVerificationPage = React.lazy(() => import("./pages/EmailVerificationPage"));
const PromotionalPage = React.lazy(() => import("./pages/PromotionalPage"));
const TrackingRedirect = React.lazy(() => import("./pages/TrackingRedirect"));

// Shop / Digital Products
const ShopLayout = React.lazy(() => import("./components/Shop"));
const MyProducts = React.lazy(() => import("./components/Shop/MyProducts"));
const ProductCreate = React.lazy(() => import("./components/Shop/ProductCreate"));
const ProductStatistics = React.lazy(() => import("./components/Shop/ProductStatistics"));
const ProductList = React.lazy(() => import("./components/Shop/ProductList"));
const ProductDetail = React.lazy(() => import("./components/Shop/ProductDetail"));
const MyPurchases = React.lazy(() => import("./components/Shop/MyPurchases"));

// CRM Module
const CrmLayout = React.lazy(() => import("./components/CRM/CrmLayout"));
const CrmDashboard = React.lazy(() => import("./components/CRM/Dashboard"));
const FanList = React.lazy(() => import("./components/CRM/FanList"));
const FanProfile = React.lazy(() => import("./components/CRM/FanProfile"));
const ScriptsPage = React.lazy(() => import("./components/CRM/ScriptsPage"));
const AutomationsPage = React.lazy(() => import("./components/CRM/AutomationsPage"));
const AnalyticsDashboard = React.lazy(() => import("./components/CRM/AnalyticsDashboard"));
const TeamPage = React.lazy(() => import("./components/CRM/TeamPage"));
const VaultPage = React.lazy(() => import("./components/CRM/VaultPage"));
const AiInsightsPage = React.lazy(() => import("./components/CRM/AiInsightsPage"));

const CreatorGiftManager = React.lazy(() => import("./components/LiveStream/Gifts/CreatorGiftManager"));

// Livestream components are lazy-loaded so @stream-io/video-react-sdk
// (which pulls in Node.js 'crypto' deps) is NOT bundled into the main chunk.
// This prevents the "Cannot find module 'crypto'" error on all other pages.
const PreCallScreen = React.lazy(() => import("./components/LiveStream/PreCall/PreCallScreen"));
const LiveStreamPage = React.lazy(() => import("./components/LiveStream/Stream/LiveStreamPage"));
const ViewerStreamPage = React.lazy(() => import("./components/LiveStream/Stream/ViewerStreamPage"));
const LiveListPage = React.lazy(() => import("./components/LiveStream/LiveListPage"));
const ContactPage = React.lazy(() => import("./pages/ContactPage"));

// Fallback shown while a lazy route chunk downloads. Kept intentionally
// tiny/dependency-free since it's part of the always-eager main chunk.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0a0a0a]">
    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
  </div>
);

export default function App() {
  return (
    <InstallGate>
    <Suspense fallback={<RouteFallback />}>
    <Routes>

      {/* Public routes - no authentication required */}
      <Route index element={<LandingPage />} />
      <Route path="login" element={<LoginPage />} />
      <Route path="register" element={<RegisterPage />} />
      <Route path="forgot-password" element={<ForgotPasswordPage />} />
      <Route path="verify-email" element={<EmailVerificationPage />} />
      <Route path="logout" element={<Logout />} />
      <Route path="payment-success" element={<PaymentSuccess />} />
      <Route path="payment-cancelled" element={<PaymentSuccess />} />
      <Route path="team-invite/:token" element={<TeamInvitePage />} />
      <Route path="agency-invite/:token" element={<AgencyInviteLanding />} />

      {/* Public promotional link routes - no authentication required */}
      <Route path="trial/:code" element={<PromotionalPage type="trial" />} />
      <Route path="offer/:campaignId" element={<PromotionalPage type="campaign" />} />
      <Route path="ref/:code" element={<TrackingRedirect />} />

      {/* Public pay-per-link viewer (no auth) */}
      <Route path="p/:slug" element={<PayLinkPublic />} />

      {/* Static pages (Terms, Privacy, Cookies, and admin-created CMS pages) —
          public, since login/register/landing link to these while logged out.
          Explicit slugs take precedence over the page/:slug catch-all below
          regardless of declaration order (React Router matches static path
          segments before dynamic ones). */}
      <Route path="page/terms-of-service" element={<TermsOfService />} />
      <Route path="page/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="page/cookie-policy" element={<CookiePolicy />} />
      <Route path="page/:slug" element={<StaticPage />} />

      {/* Public profile — anonymous viewers see the profile; action
          buttons (subscribe, follow, message, tip, comment) bounce
          to /login?returnTo= when no session is present. */}
      <Route path="profile" element={<UserProfile />} />

      {/* Protected routes - authentication required */}
      <Route element={<AuthWrapper />}>
        <Route path="home" element={<Homepage />} />
        <Route path="post/:postId" element={<SinglePost />} />
        <Route path="become-creator" element={<BecomeCreator />} />

        {/* Agency mode */}
        <Route path="agency/apply" element={<AgencyApply />} />
        <Route path="agency" element={<AgencyDashboard />} />
        <Route path="agency/inbox" element={<AgencyInbox />} />
        <Route path="agency/settings" element={<AgencyOwnerSettings />} />

         {/* Creator-only routes (with team permission guards) */}
         <Route path="my/subscriptions" element={<CreatorRoute><PermissionRoute perms={['manage_subscription_price']}><PlansManagement /></PermissionRoute></CreatorRoute>} />
         <Route path="my/statements" element={<CreatorRoute><PermissionRoute perms={['view_statements']}><EarningsStatementPage /></PermissionRoute></CreatorRoute>} />
         <Route path="my/statistics" element={<CreatorRoute><PermissionRoute perms={['view_analytics']}><CreatorDashboard /></PermissionRoute></CreatorRoute>} />
         <Route path="my/statistics/overview" element={<CreatorRoute><PermissionRoute perms={['view_analytics']}><CreatorStatsDashboard /></PermissionRoute></CreatorRoute>} />
         <Route path="my/statistics/engagements" element={<CreatorRoute><PermissionRoute perms={['view_analytics']}><EngagementStatistics /></PermissionRoute></CreatorRoute>} />
         <Route path="my/statistics/reach" element={<CreatorRoute><PermissionRoute perms={['view_analytics']}><ReachStatistics /></PermissionRoute></CreatorRoute>} />
         <Route path="my/statistics/fans" element={<CreatorRoute><PermissionRoute perms={['view_analytics']}><StatisticsPage /></PermissionRoute></CreatorRoute>} />
         <Route path="my/queue" element={<CreatorRoute><PermissionRoute perms={['manage_queue']}><QueueCalendar /></PermissionRoute></CreatorRoute>} />
         <Route path="my/livestream" element={<CreatorRoute><PermissionRoute perms={['manage_livestream']}><Suspense fallback={null}><LiveStreamProvider isCreator={true}><PreCallScreen /></LiveStreamProvider></Suspense></PermissionRoute></CreatorRoute>} />
         <Route path="my/livestream/live" element={<CreatorRoute><PermissionRoute perms={['manage_livestream']}><Suspense fallback={null}><LiveStreamProvider isCreator={true}><LiveStreamPage /></LiveStreamProvider></Suspense></PermissionRoute></CreatorRoute>} />
         <Route path="my/vaults" element={<CreatorRoute><PermissionRoute perms={['manage_vault']}><Vaults /></PermissionRoute></CreatorRoute>}>
           <Route path="/my/vaults/allmedia" element={<Allmedia />} />
         </Route>
         <Route path="my/pay-links" element={<CreatorRoute><PermissionRoute perms={['manage_vault']}><PayLinks /></PermissionRoute></CreatorRoute>} />
         <Route path="my/pay-links/:id" element={<CreatorRoute><PermissionRoute perms={['manage_vault']}><PayLinkStats /></PermissionRoute></CreatorRoute>} />
         <Route path="my/statisticsdash" element={<CreatorRoute><PermissionRoute perms={['view_analytics']}><StatisticsDashboard /></PermissionRoute></CreatorRoute>} />
         <Route path="my/wallet" element={<PermissionRoute perms={['view_wallet']}><WalletPage /></PermissionRoute>} />
         <Route path="my/banking" element={<CreatorRoute><PermissionRoute perms={['view_banking']}><BankingPage /></PermissionRoute></CreatorRoute>} />
         {/* Shop / Digital Products - Creator routes */}
         <Route path="my/shop" element={<CreatorRoute><PermissionRoute perms={['manage_shop']}><ShopLayout /></PermissionRoute></CreatorRoute>}>
           <Route index element={<MyProducts />} />
           <Route path="create" element={<ProductCreate />} />
           <Route path="edit/:id" element={<ProductCreate />} />
           <Route path="statistics" element={<ProductStatistics />} />
         </Route>

         {/* CRM Module - Creator routes (shared layout, granular perms per sub-route) */}
         <Route path="my/crm" element={<CreatorRoute><CrmLayout /></CreatorRoute>}>
           <Route index element={<PermissionRoute perms={['view_dashboard']}><CrmDashboard /></PermissionRoute>} />
           <Route path="fans" element={<PermissionRoute perms={['view_fans']}><FanList /></PermissionRoute>} />
           <Route path="fans/:fanId" element={<PermissionRoute perms={['view_fans']}><FanProfile /></PermissionRoute>} />
           <Route path="scripts" element={<PermissionRoute perms={['manage_scripts']}><ScriptsPage /></PermissionRoute>} />
           <Route path="automations" element={<PermissionRoute perms={['manage_automations']}><AutomationsPage /></PermissionRoute>} />
           <Route path="vault" element={<PermissionRoute perms={['manage_crm_vault']}><VaultPage /></PermissionRoute>} />
           <Route path="team" element={<PermissionRoute perms={['manage_team']}><TeamPage /></PermissionRoute>} />
           <Route path="analytics" element={<PermissionRoute perms={['view_analytics']}><AnalyticsDashboard /></PermissionRoute>} />
           <Route path="insights" element={<PermissionRoute perms={['view_insights']}><AiInsightsPage /></PermissionRoute>} />
           <Route path="gifts" element={<PermissionRoute perms={['manage_livestream']}><CreatorGiftManager /></PermissionRoute>} />
         </Route>

         <Route path="my/chats/mass-message" element={<CreatorRoute><PermissionRoute perms={['mass_messaging']}><MassMessage /></PermissionRoute></CreatorRoute>} />
         <Route path="my/collections" element={<CreatorRoute><PermissionRoute perms={['manage_collections']}><Collections /></PermissionRoute></CreatorRoute>}>
            <Route path="/my/collections/bookmarks" element={<Bookmarklist />} />
            <Route path="/my/collections/list/:listId" element={<ListDetail />} />
            {/* Subscriber Collections */}
            <Route path="/my/collections/active-subscribers" element={<Userlist listType="active-subscribers" />} />
            <Route path="/my/collections/recent-subscribers" element={<Userlist listType="recent-subscribers" />} />
            <Route path="/my/collections/expired-subscribers" element={<Userlist listType="expired-subscribers" />} />
            <Route path="/my/collections/renewing-soon" element={<Userlist listType="renewing-soon" />} />
            <Route path="/my/collections/top-spenders" element={<Userlist listType="top-spenders" />} />
            <Route path="/my/collections/top-fans" element={<Userlist listType="top-fans" />} />
            <Route path="/my/collections/free-subscribers" element={<Userlist listType="free-subscribers" />} />
            <Route path="/my/collections/following" element={<Userlist listType="following" />} />
            <Route path="/my/collections/muted" element={<Userlist listType="muted" />} />
            <Route path="/my/collections/restricted" element={<Userlist listType="restricted" />} />
            <Route path="/my/collections/blocked" element={<Userlist listType="blocked" />} />
            <Route path="/my/collections/with-rebill" element={<Userlist listType="with-rebill" />} />
            <Route path="/my/collections/without-rebill" element={<Userlist listType="without-rebill" />} />
            <Route path="/my/collections/attention-needed" element={<Userlist listType="attention-needed" />} />
         </Route>

         {/* Bookmarks - available to all authenticated users */}
         <Route path="my/bookmarks" element={<BookmarksPage />} />

         {/* Routes available to all authenticated users */}
         <Route path="live" element={<Suspense fallback={null}><LiveListPage /></Suspense>} />
         <Route path="help" element={<HelpCenter />} />
         <Route path="help/:categorySlug" element={<HelpCenter />} />
         <Route path="help/:categorySlug/:topicSlug" element={<HelpCenter />} />
         <Route path="contact" element={<Suspense fallback={null}><ContactPage /></Suspense>} />
         <Route path="my/charts" element={<RechartsChartTypes />} />
         <Route path="my/payments&cards" element={<PaymentCardsPage />} />
         <Route path="my/settings" element={<PermissionRoute perms={['view_settings']}><Settings /></PermissionRoute>} >
           <Route path='/my/settings/profile' element={<Settingsprofile />} />
           <Route path='/my/settings/account' element={<Accountsets/>} />
           <Route path='/my/settings/phone' element={<PhoneNumberSettings />} />
           <Route path='/my/settings/privacy-and-safety' element={<PrivacyAndSafetySettings />} />
           <Route path="/my/settings/subscription-price-and-bundles" element={<CreatorRoute><SubscriptionSettings /></CreatorRoute>} />
           <Route path='/my/settings/fans-and-following' element={<FansFollowingSettings />} />
           <Route path='/my/settings/notifications' element={<NotificationSettings  />} />
           <Route path='/my/settings/chats' element={<ChatSettings />} />
           <Route path='/my/settings/ai-agent' element={<CreatorAiAgentSettings />} />
           <Route path='/my/settings/streaming' element={<StreamingSettings />} />
           <Route path='/my/settings/display' element={<DisplaySettings />} />
           <Route path='/my/settings/qr-code' element={<QrCodeSettings />} />
           <Route path='/my/settings/story' element={<StorySettings />} />
           <Route path='/my/settings/social-media' element={<SocialMedia />} />
           {/* <Route path='/my/settings/spotify' element={<Spotify />} /> */}
           {/* <Route path='/my/settings/spring-store' element={<SpringStore />} /> */}
           {/* <Route path='/my/settings/things-i-like' element={<ThingsILike />} /> */}
           {/* <Route path='/my/settings/web3-avatar' element={<Web3Avatar />} /> */}
           {/* <Route path='/my/settings/nfts' element={<NFTs />} /> */}
           <Route path='/my/settings/my-subscriptions' element={<CreatorRoute><MySubscriptions /></CreatorRoute>} />
           <Route path='/my/settings/my-subscribers' element={<CreatorRoute><MySubscribers /></CreatorRoute>} />
          </Route>

          <Route path="/notifications" element={<PermissionRoute perms={['view_notifications']}><Notifications /></PermissionRoute>} >
          <Route path="/notifications/all" element={<All />} />
          </Route>
          <Route path="my/chats" element={<PermissionRoute perms={['view_chats']}><Chats /></PermissionRoute>} >
            <Route path="Openmsg/:conversationId" element={<Openmsg />} />
            <Route path="user/:userId" element={<Openmsg />} />
          </Route>

          {/* Shop / Digital Products - Browse & Purchase */}
          <Route path="shop" element={<ProductList />} />
          <Route path="shop/product/:id" element={<ProductDetail />} />
          <Route path="my/purchases" element={<MyPurchases />} />

          {/* Fan Comments */}
          <Route path="my/fans/:userId/comments" element={<UserComments />} />

          {/* Admin Routes */}
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserEdit />} />
            <Route path="verifications" element={<CreatorVerifications />} />
            <Route path="payments" element={<PaymentSettings />} />
            <Route path="payouts" element={<PayoutManagement />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="storage" element={<StorageSettings />} />
            <Route path="notifications" element={<AdminNotificationSettings />} />
            <Route path="email" element={<AdminEmailSettings />} />
            <Route path="appearance" element={<AppearanceSettings />} />
            <Route path="pwa" element={<PwaSettings />} />
            <Route path="agencies" element={<AgencyApplications />} />
            <Route path="agency-settings" element={<AgencySettings />} />
            <Route path="authentication" element={<AuthenticationSettings />} />
            <Route path="landing-pages" element={<LandingPageSettings />} />
            <Route path="moderation" element={<ModerationPage />} />
            <Route path="messages" element={<MessagesAdmin />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="integrations" element={<IntegrationSettings />} />
            <Route path="livestream" element={<LivestreamSettings />} />
            <Route path="gifts" element={<GiftManagement />} />
            <Route path="ai-agent" element={<AiAgentSettings />} />
            <Route path="license" element={<LicenseSettings />} />
            <Route path="posts" element={<PostsManagement />} />
            <Route path="subscriptions" element={<SubscriptionsManagement />} />
            <Route path="transactions" element={<TransactionsManagement />} />
            <Route path="announcements" element={<AnnouncementsManagement />} />
            <Route path="contact-messages" element={<ContactMessagesManagement />} />
            <Route path="help" element={<HelpManagement />} />
            <Route path="maintenance" element={<MaintenanceMode />} />
            <Route path="shop" element={<ShopManagement />} />
            <Route path="custom-code" element={<CustomCode />} />
            <Route path="platform" element={<AdminPlatformSettings />} />
            <Route path="pages" element={<PagesManagement />} />
          </Route>

          {/* Live stream viewer route */}
          <Route path="live/:username" element={<Suspense fallback={null}><ViewerStreamPage /></Suspense>} />
          </Route>

          {/* Dynamic username route — public, must be the very last
              top-level route so it doesn't shadow /login, /register, etc.
              Anonymous viewers see the profile; action buttons that need
              an account bounce to /login?returnTo=. */}
          <Route path=":username" element={<UserProfile />} />
      </Routes>
      </Suspense>
    </InstallGate>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Wait for the active language's translations to be loaded before the first
// render, so a session with a non-English language persisted paints once,
// correctly, instead of flashing English and then re-rendering in Persian.
i18nReady.finally(() => {
  root.render(
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider>
          <LanguageProvider>
            <SiteConfigProvider>
            <AuthProvider>
              <AppProvider>
                <VideoCallProvider>
                  <BrowserRouter>
                    <StoryProvider>
                      <ImpersonationBanner />
                      <ToastNotificationManager />
                      <InstallPwaBanner />
                      <PaymentRedirectToast />
                      <App />
                      <VideoCallRoot />
                      <AgeGateModal />
                    </StoryProvider>
                  </BrowserRouter>
                </VideoCallProvider>
              </AppProvider>
            </AuthProvider>
            </SiteConfigProvider>
          </LanguageProvider>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
});

// Service worker registration. Gated by /api/site-config so admins
// can disable PWA behaviour without redeploying. We don't block app
// startup on this — it fires after window load so the SPA renders
// first, SW activates in the background.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    fetch(getApiUrl('/site-config'))
      .then((r) => r.ok ? r.json() : null)
      .then((cfg) => {
        if (cfg?.data?.pwa?.enabled === false) return; // admin opt-out
        navigator.serviceWorker.register('/service-worker.js')
          .catch((err) => console.warn('Service worker registration failed:', err));
      })
      .catch(() => { /* offline / first load — try again next visit */ });
  });
}
